import React, { useState, useEffect } from "react";
import { useApi } from "../../../hooks/use-api";
import { useQuestionBankHandler } from "../handler";
import { useSingleton } from "../../../hooks/use-singleton";
import {
  apiTestCategories,
  apiTestExams,
  apiTestClasses,
  apiTestChapters,
  apiTestSubTopics,
} from "../../../utils/api-routes";
import { COOKIE_ACCESS_MAIL } from "../../../utils/storage-constants";
import { CustomDatePicker } from "../../test-creation/custom-date-picker";
import "./style.sass";

const formatDurationDisplay = (val) => {
  if (val === null || val === undefined || val === "" || isNaN(Number(val))) {
    return "-";
  }
  const mins = Number(val);
  if (mins <= 0) return "-";
  if (mins < 60) {
    return `${mins} mins`;
  }
  const hrs = mins / 60;
  if (Number.isInteger(hrs)) {
    return `${hrs} ${hrs === 1 ? "hr" : "hrs"}`;
  }
  const rounded = Number(hrs.toFixed(2));
  return `${rounded} hrs`;
};

const formatDateDisplay = (dateStr, timeStr = "") => {
  if (!dateStr && !timeStr) return "-";
  
  let formattedDate = "";
  if (dateStr) {
    if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
      const parts = dateStr.split("T")[0].split("-");
      if (parts.length === 3) {
        const year = parts[0];
        const monthIdx = parseInt(parts[1], 10) - 1;
        const day = parseInt(parts[2], 10);
        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        if (monthNames[monthIdx]) {
          formattedDate = `${day} ${monthNames[monthIdx]} ${year}`;
        }
      }
    }
    if (!formattedDate) {
      formattedDate = dateStr;
    }
  }

  let formattedTime = "";
  if (timeStr) {
    if (/^\d{1,2}:\d{2}/.test(timeStr)) {
      const timeParts = timeStr.split(":");
      let hrs = parseInt(timeParts[0], 10);
      const mins = timeParts[1];
      const ampm = hrs >= 12 ? "PM" : "AM";
      hrs = hrs % 12 || 12;
      const hrsStr = hrs < 10 ? `${hrs}` : `${hrs}`;
      formattedTime = `${hrsStr}:${mins} ${ampm}`;
    } else {
      formattedTime = timeStr;
    }
  }

  if (formattedDate && formattedTime) {
    return `${formattedDate} ${formattedTime}`;
  }
  return formattedDate || formattedTime || "-";
};

export const CreatedTestsTable = ({ onSelectTest }) => {
  const [page, setPage] = useState(1);
  const pageSize = 8;
  const [totalCount, setTotalCount] = useState(0);
  const [showingText, setShowingText] = useState("Showing 0 of 0");
  const [loading, setLoading] = useState(false);

  const api = useApi();
  const { fetchCreatedQuestionPapers } = useQuestionBankHandler(api);
  const { user, mail, role } = useSingleton();
  const teacherMail =
    mail ||
    user?.data?.email ||
    user?.email ||
    (typeof window !== "undefined" &&
      (localStorage.getItem(COOKIE_ACCESS_MAIL) ||
        localStorage.getItem("email") ||
        localStorage.getItem("user_email") ||
        localStorage.getItem("__FANAMA_USER_EMAIL__"))) ||
    "";
  const teacherRole = role || user?.data?.role || user?.role || "teacher";

  const [tests, setTests] = useState([]);

  // Table Filters State
  const [tableFilters, setTableFilters] = useState({
    testName: "",
    testCategory: "",
    exam: "",
    className: "",
    chapter: "",
    subTopic: "",
    date: "",
  });

  // Dynamic Options States
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [examOptions, setExamOptions] = useState([]);
  const [classOptions, setClassOptions] = useState([]);
  const [chapterOptions, setChapterOptions] = useState([]);
  const [subTopicOptions, setSubTopicOptions] = useState([]);

  // Fetch Categories
  useEffect(() => {
    const fetchCategories = async () => {
      if (!teacherMail) return;
      try {
        const res = await api.get(apiTestCategories(teacherMail, teacherRole));
        const data = res?.data || res || {};
        const list = data.categories || (Array.isArray(data) ? data : []);
        setCategoryOptions(Array.isArray(list) ? list : []);
      } catch (e) {
        console.log("Error fetching test categories", e);
      }
    };
    fetchCategories();
  }, [teacherMail, teacherRole]);

  // Fetch Exams
  useEffect(() => {
    const fetchExams = async () => {
      if (!teacherMail) return;
      try {
        const res = await api.get(apiTestExams(teacherMail, teacherRole));
        const data = res?.data || res || {};
        const list = data.exams || (Array.isArray(data) ? data : []);
        setExamOptions(Array.isArray(list) ? list : []);
      } catch (e) {
        console.log("Error fetching test exams", e);
      }
    };
    fetchExams();
  }, [teacherMail, teacherRole]);

  // Fetch Classes
  useEffect(() => {
    const fetchClasses = async () => {
      if (!teacherMail || !tableFilters.exam) {
        setClassOptions([]);
        return;
      }
      try {
        const res = await api.get(
          apiTestClasses(tableFilters.exam, teacherMail, teacherRole)
        );
        const data = res?.data || res || {};
        const list = data.classes || (Array.isArray(data) ? data : []);
        setClassOptions(Array.isArray(list) ? list : []);
      } catch (e) {
        console.log("Error fetching test classes", e);
        setClassOptions([]);
      }
    };
    fetchClasses();
  }, [tableFilters.exam, teacherMail, teacherRole]);

  // Fetch Chapters
  useEffect(() => {
    const fetchChapters = async () => {
      if (!teacherMail || !tableFilters.className) {
        setChapterOptions([]);
        return;
      }
      try {
        const res = await api.get(
          apiTestChapters(
            tableFilters.exam || "",
            tableFilters.className || "",
            teacherMail,
            teacherRole
          )
        );
        const data = res?.data || res || {};
        const list = data.chapters || (Array.isArray(data) ? data : []);
        setChapterOptions(Array.isArray(list) ? list : []);
      } catch (e) {
        console.log("Error fetching test chapters", e);
      }
    };
    fetchChapters();
  }, [tableFilters.exam, tableFilters.className, teacherMail, teacherRole]);

  // Fetch SubTopics
  useEffect(() => {
    const fetchSubTopics = async () => {
      if (!teacherMail) return;
      try {
        const res = await api.get(
          apiTestSubTopics(
            tableFilters.exam || "",
            tableFilters.chapter || "",
            teacherMail,
            teacherRole
          )
        );
        const data = res?.data || res || {};
        const list = data.sub_topics || (Array.isArray(data) ? data : []);
        setSubTopicOptions(Array.isArray(list) ? list : []);
      } catch (e) {
        console.log("Error fetching test sub-topics", e);
      }
    };
    fetchSubTopics();
  }, [tableFilters.exam, tableFilters.chapter, teacherMail, teacherRole]);

  const [openFilterDropdown, setOpenFilterDropdown] = useState(null);
  const filterBarRef = React.useRef(null);

  // Close filter dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterBarRef.current && !filterBarRef.current.contains(event.target)) {
        setOpenFilterDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const [debouncedTestName, setDebouncedTestName] = useState("");

  // Debounce testName input to avoid making API calls on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTestName(tableFilters.testName);
    }, 400);
    return () => clearTimeout(timer);
  }, [tableFilters.testName]);

  useEffect(() => {
    let isMounted = true;
    const loadQuestionPapers = async () => {
      if (!teacherMail) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const queryFilters = { ...tableFilters, testName: debouncedTestName };
        const res = await fetchCreatedQuestionPapers({ page, pageSize, filters: queryFilters });
        if (isMounted) {
          const paperList = res?.tests || res?.question_papers || res?.question_papers_list || (Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : []);
          if (paperList && paperList.length > 0) {
            const mapped = paperList.map((qp, idx) => ({
              id: qp.test_id || qp.room_code || qp.paper_id || qp.id || idx + 1,
              testName: qp.test_name || qp.testName || "Untitled Test",
              subject: qp.subject || "Biology",
              testCategory: qp.test_category || qp.testCategory || "",
              examType: qp.exam || qp.exam_type || qp.examType || "NEET",
              class: qp.class || qp.clas || qp.classLevel || "Class 12",
              chapter: qp.chapter || "-",
              subTopic: qp.sub_topic || qp.subTopic || "-",
              dateTime: formatDateDisplay(
                qp.test_date || qp.scheduled_date || (qp.created_at ? qp.created_at.split("T")[0] : qp.date_time || qp.dateTime),
                qp.test_time || qp.scheduled_time || (qp.created_at ? qp.created_at.split("T")[1]?.slice(0, 5) : "")
              ),
              durationMinutes:
                qp.duration_minutes !== undefined
                  ? qp.duration_minutes
                  : qp.durationMinutes || null,
            }));
            setTests(mapped);
            const total = res.total || res.total_count || mapped.length;
            setTotalCount(total);
            const startIdx = (page - 1) * pageSize + 1;
            const endIdx = Math.min(page * pageSize, total);
            setShowingText(res.showing || `Showing ${startIdx}–${endIdx} of ${total}`);
          } else {
            setTests([]);
            setTotalCount(0);
            setShowingText("Showing 0 of 0");
          }
        }
      } catch (err) {
        console.error("Failed to fetch question papers:", err);
        if (isMounted) {
          setTests([]);
          setTotalCount(0);
          setShowingText("Showing 0 of 0");
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadQuestionPapers();
    return () => {
      isMounted = false;
    };
  }, [page, debouncedTestName, tableFilters.testCategory, tableFilters.exam, tableFilters.className, tableFilters.chapter, tableFilters.subTopic, tableFilters.date, teacherMail]);

  const handleTableFilterChange = (key, value) => {
    setTableFilters((prev) => {
      const updated = { ...prev, [key]: value };
      if (key === "exam") {
        updated.className = "";
        updated.chapter = "";
        updated.subTopic = "";
      }
      if (key === "className") {
        updated.chapter = "";
        updated.subTopic = "";
      }
      if (key === "chapter") {
        updated.subTopic = "";
      }
      return updated;
    });
    setPage(1);
  };

  const handleClearTableFilters = () => {
    setTableFilters({
      testName: "",
      testCategory: "",
      exam: "",
      className: "",
      chapter: "",
      subTopic: "",
      date: "",
    });
    setPage(1);
  };

  const hasActiveFilters = Object.values(tableFilters).some((val) => Boolean(val));

  const allTestNameOptions = Array.from(
    new Set(tests.map((t) => t.testName || t.test_name).filter(Boolean))
  );

  const testNameSuggestions = tableFilters.testName.trim()
    ? allTestNameOptions.filter((name) =>
        name.toLowerCase().includes(tableFilters.testName.toLowerCase())
      )
    : [];

  const allCategoryOptions = Array.from(
    new Set([
      ...categoryOptions,
      ...tests.map((t) => t.testCategory).filter(Boolean),
    ])
  );

  const allExamOptions = Array.from(
    new Set([
      ...examOptions,
      ...tests.map((t) => t.examType).filter(Boolean),
    ])
  );

  const allClassOptions = classOptions;
  const allChapterOptions = chapterOptions;
  const allSubTopicOptions = subTopicOptions;

  const displayedTests = tests.filter((item) => {
    if (
      tableFilters.testName &&
      !item.testName.toLowerCase().includes(tableFilters.testName.toLowerCase())
    ) {
      return false;
    }
    if (
      tableFilters.testCategory &&
      item.testCategory &&
      item.testCategory.toLowerCase() !== tableFilters.testCategory.toLowerCase()
    ) {
      return false;
    }
    if (
      tableFilters.exam &&
      item.examType &&
      item.examType.toLowerCase() !== tableFilters.exam.toLowerCase() &&
      !item.examType.toLowerCase().includes(tableFilters.exam.toLowerCase())
    ) {
      return false;
    }
    if (
      tableFilters.className &&
      item.class &&
      item.class.toLowerCase() !== tableFilters.className.toLowerCase() &&
      !item.class.toLowerCase().includes(tableFilters.className.toLowerCase())
    ) {
      return false;
    }
    if (
      tableFilters.chapter &&
      item.chapter &&
      !item.chapter.toLowerCase().includes(tableFilters.chapter.toLowerCase())
    ) {
      return false;
    }
    if (
      tableFilters.subTopic &&
      item.subTopic &&
      !item.subTopic.toLowerCase().includes(tableFilters.subTopic.toLowerCase())
    ) {
      return false;
    }
    if (
      tableFilters.date &&
      item.dateTime &&
      !item.dateTime.includes(tableFilters.date)
    ) {
      return false;
    }
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const getPaginationItems = (currentPage, totalPagesCount) => {
    if (totalPagesCount <= 5) {
      return Array.from({ length: totalPagesCount }, (_, i) => i + 1);
    }
    const items = [1];
    if (currentPage > 3) {
      items.push("...");
    }
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPagesCount - 1, currentPage + 1);
    for (let i = start; i <= end; i++) {
      if (!items.includes(i)) {
        items.push(i);
      }
    }
    if (currentPage < totalPagesCount - 2) {
      items.push("...");
    }
    if (!items.includes(totalPagesCount)) {
      items.push(totalPagesCount);
    }
    return items;
  };

  return (
    <div className="created_tests_container">
      {/* Filters Bar */}
      <div className="existing-tests-filter-card" ref={filterBarRef}>
        <div className="filter-card-header">
          <div className="filter-card-title">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
            </svg>
            <span>Filter Tests</span>
          </div>
          {hasActiveFilters && (
            <button className="clear-filters-btn" onClick={handleClearTableFilters}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
              <span>Reset</span>
            </button>
          )}
        </div>

        <div className="filters-grid">
          {/* 1. Test Name */}
          <div className="filter-item">
            <label>Test Name</label>
            <div className="test-name-input-wrapper">
              <input
                type="text"
                placeholder="Search test name..."
                value={tableFilters.testName}
                onChange={(e) => {
                  handleTableFilterChange("testName", e.target.value);
                  setOpenFilterDropdown("testName");
                }}
                onFocus={() => setOpenFilterDropdown("testName")}
              />
            </div>
            {openFilterDropdown === "testName" && tableFilters.testName.trim() !== "" && (
              <div className="filter-dropdown-popover test-name-suggestions-popover">
                {testNameSuggestions.length > 0 ? (
                  testNameSuggestions.map((suggestion, idx) => (
                    <div
                      key={idx}
                      className="popover-item suggestion-item"
                      onClick={() => {
                        handleTableFilterChange("testName", suggestion);
                        setOpenFilterDropdown(null);
                      }}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#6e3ff3" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                      </svg>
                      <span>{suggestion}</span>
                    </div>
                  ))
                ) : (
                  <div className="popover-item no-suggestions">
                    No matching test names
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 2. Test Category */}
          <div className="filter-item">
            <label>Test Category</label>
            <div
              className={`filter-select-trigger ${openFilterDropdown === "category" ? "active" : ""}`}
              onClick={() => setOpenFilterDropdown(openFilterDropdown === "category" ? null : "category")}
            >
              <span className="selected-val">{tableFilters.testCategory || "All Categories"}</span>
              <svg className={`chevron ${openFilterDropdown === "category" ? "open" : ""}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </div>
            {openFilterDropdown === "category" && (
              <div className="filter-dropdown-popover">
                <div
                  className={`popover-item ${tableFilters.testCategory === "" ? "selected" : ""}`}
                  onClick={() => {
                    handleTableFilterChange("testCategory", "");
                    setOpenFilterDropdown(null);
                  }}
                >
                  All Categories
                </div>
                {allCategoryOptions.map((cat, idx) => (
                  <div
                    key={idx}
                    className={`popover-item ${tableFilters.testCategory === cat ? "selected" : ""}`}
                    onClick={() => {
                      handleTableFilterChange("testCategory", cat);
                      setOpenFilterDropdown(null);
                    }}
                  >
                    {cat}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 3. Exam */}
          <div className="filter-item">
            <label>Exam</label>
            <div
              className={`filter-select-trigger ${openFilterDropdown === "exam" ? "active" : ""}`}
              onClick={() => setOpenFilterDropdown(openFilterDropdown === "exam" ? null : "exam")}
            >
              <span className="selected-val">{tableFilters.exam || "All Exams"}</span>
              <svg className={`chevron ${openFilterDropdown === "exam" ? "open" : ""}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </div>
            {openFilterDropdown === "exam" && (
              <div className="filter-dropdown-popover">
                <div
                  className={`popover-item ${tableFilters.exam === "" ? "selected" : ""}`}
                  onClick={() => {
                    handleTableFilterChange("exam", "");
                    setOpenFilterDropdown(null);
                  }}
                >
                  All Exams
                </div>
                {allExamOptions.map((ex, idx) => (
                  <div
                    key={idx}
                    className={`popover-item ${tableFilters.exam === ex ? "selected" : ""}`}
                    onClick={() => {
                      handleTableFilterChange("exam", ex);
                      setOpenFilterDropdown(null);
                    }}
                  >
                    {ex}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 4. Class */}
          <div className="filter-item">
            <label>Class</label>
            <div
              className={`filter-select-trigger ${openFilterDropdown === "class" ? "active" : ""}`}
              onClick={() => setOpenFilterDropdown(openFilterDropdown === "class" ? null : "class")}
            >
              <span className="selected-val">{tableFilters.className || "All Classes"}</span>
              <svg className={`chevron ${openFilterDropdown === "class" ? "open" : ""}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </div>
            {openFilterDropdown === "class" && (
              <div className="filter-dropdown-popover">
                <div
                  className={`popover-item ${tableFilters.className === "" ? "selected" : ""}`}
                  onClick={() => {
                    handleTableFilterChange("className", "");
                    setOpenFilterDropdown(null);
                  }}
                >
                  All Classes
                </div>
                {!tableFilters.exam ? (
                  <div className="popover-item no-suggestions">Please select an Exam first</div>
                ) : allClassOptions.length === 0 ? (
                  <div className="popover-item no-suggestions">No classes found for selected exam</div>
                ) : (
                  allClassOptions.map((cls, idx) => (
                    <div
                      key={idx}
                      className={`popover-item ${tableFilters.className === cls ? "selected" : ""}`}
                      onClick={() => {
                        handleTableFilterChange("className", cls);
                        setOpenFilterDropdown(null);
                      }}
                    >
                      {cls}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* 5. Chapter */}
          <div className="filter-item">
            <label>Chapter</label>
            <div
              className={`filter-select-trigger ${openFilterDropdown === "chapter" ? "active" : ""}`}
              onClick={() => setOpenFilterDropdown(openFilterDropdown === "chapter" ? null : "chapter")}
            >
              <span className="selected-val">{tableFilters.chapter || "All Chapters"}</span>
              <svg className={`chevron ${openFilterDropdown === "chapter" ? "open" : ""}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </div>
            {openFilterDropdown === "chapter" && (
              <div className="filter-dropdown-popover">
                <div
                  className={`popover-item ${tableFilters.chapter === "" ? "selected" : ""}`}
                  onClick={() => {
                    handleTableFilterChange("chapter", "");
                    setOpenFilterDropdown(null);
                  }}
                >
                  All Chapters
                </div>
                {!tableFilters.className ? (
                  <div className="popover-item no-suggestions">Please select a Class first</div>
                ) : allChapterOptions.length === 0 ? (
                  <div className="popover-item no-suggestions">No chapters found for selected class</div>
                ) : (
                  allChapterOptions.map((ch, idx) => (
                    <div
                      key={idx}
                      className={`popover-item ${tableFilters.chapter === ch ? "selected" : ""}`}
                      onClick={() => {
                        handleTableFilterChange("chapter", ch);
                        setOpenFilterDropdown(null);
                      }}
                    >
                      {ch}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* 6. Subtopic */}
          <div className="filter-item">
            <label>Sub Topic</label>
            <div
              className={`filter-select-trigger ${openFilterDropdown === "subTopic" ? "active" : ""}`}
              onClick={() => setOpenFilterDropdown(openFilterDropdown === "subTopic" ? null : "subTopic")}
            >
              <span className="selected-val">{tableFilters.subTopic || "All Sub Topics"}</span>
              <svg className={`chevron ${openFilterDropdown === "subTopic" ? "open" : ""}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </div>
            {openFilterDropdown === "subTopic" && (
              <div className="filter-dropdown-popover">
                <div
                  className={`popover-item ${tableFilters.subTopic === "" ? "selected" : ""}`}
                  onClick={() => {
                    handleTableFilterChange("subTopic", "");
                    setOpenFilterDropdown(null);
                  }}
                >
                  All Sub Topics
                </div>
                {!tableFilters.chapter ? (
                  <div className="popover-item no-suggestions">Please select a Chapter first</div>
                ) : allSubTopicOptions.length === 0 ? (
                  <div className="popover-item no-suggestions">No subtopics found for selected chapter</div>
                ) : (
                  allSubTopicOptions.map((sub, idx) => (
                    <div
                      key={idx}
                      className={`popover-item ${tableFilters.subTopic === sub ? "selected" : ""}`}
                      onClick={() => {
                        handleTableFilterChange("subTopic", sub);
                        setOpenFilterDropdown(null);
                      }}
                    >
                      {sub}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="table_card">
        <table className="created_tests_table">
          <thead>
            <tr>
              <th>Test Name</th>
              <th>Subject</th>
              <th>Exam Type</th>
              <th>Class</th>
              <th>Chapter</th>
              <th>Sub Topic</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" style={{ textAlign: "center", padding: "28px", color: "#6B7280" }}>
                  Loading question papers...
                </td>
              </tr>
            ) : displayedTests.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ textAlign: "center", padding: "28px", color: "#6B7280" }}>
                  No created question papers found.
                </td>
              </tr>
            ) : (
              displayedTests.map((row) => (
                <tr key={row.id} onClick={() => onSelectTest(row)}>
                  <td className="test_name_cell">{row.testName}</td>
                  <td>{row.subject}</td>
                  <td>{row.examType}</td>
                  <td>{row.class}</td>
                  <td>{row.chapter}</td>
                  <td>{row.subTopic}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="table_footer">
        <div className="showing_text">{showingText}</div>
        <div className="pagination">
          <button
            className="page_nav"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            {`<`}
          </button>
          {getPaginationItems(page, totalPages).map((item, idx) => {
            if (item === "...") {
              return (
                <span key={`dots-${idx}`} className="dots">
                  ...
                </span>
              );
            }
            return (
              <button
                key={item}
                className={`page_num ${item === page ? "active" : ""}`}
                onClick={() => setPage(item)}
              >
                {item}
              </button>
            );
          })}
          <button
            className="page_nav"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            {`>`}
          </button>
        </div>
      </div>
    </div>
  );
};
